import { useSeoMeta, useHead } from '@unhead/react';
import {CheckCircle2, Compass, MapPinned} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { SiteShell } from '@/components/SiteShell';
import { LocationLinks } from '@/components/SiteLinks';
import { areaPages } from '@/lib/areaPages';
import { normalizeCanonicalUrl } from '@/lib/seo';
import areaImg1 from '../../images/VIP House Movers Team Carrying Items.jpg';
import areaImg2 from '../../images/VIP House Movers Team Loading Removal Van.jpg';
import areaImg3 from '../../images/VIP House Movers Team Packing.jpg';
import areaImg4 from '../../images/VIP House Movers Team with happy customer.jpg';
import areaImg5 from '../../images/VIP House Movers Removal Team working.jpg';
import areaImg6 from '../../images/VIP House Movers Removal Van.jpg';
import areaImg7 from '../../images/VIP House Movers Home Removal Van.jpg';
import areaImg8 from '../../images/VIP House Movers Van infront customers house.jpg';
import areaImg9 from '../../images/VIP House Movers Team Having Laugh.jpg';
import areaImg10 from '../../images/VIP House Movers Removal Boxes.jpg';
import areasBg from '../../images/VIP House Movers Removal Team with Fleet.png';

// Curated, SEO-appropriate imagery for each area card.
// Uses moving/removal/UK-home themed Pexels photos with descriptive alts.
const AREA_IMAGES = [
  areaImg1,
  areaImg2,
  areaImg3,
  areaImg4,
  areaImg5,
  areaImg6,
  areaImg7,
  areaImg8,
  areaImg9,
  areaImg10,
];

function imageForArea(path: string) {
  let hash = 0;
  for (let i = 0; i < path.length; i += 1) hash = (hash * 31 + path.charCodeAt(i)) >>> 0;
  return AREA_IMAGES[hash % AREA_IMAGES.length];
}

export default function AreasWeCover() {
  useSeoMeta({
    title: 'Areas We Cover | Local Removals Company Wolverhampton & West Midlands | VIP House Movers',
    description: 'VIP House Movers covers Wolverhampton, Birmingham, Walsall, Dudley, Telford, Bilston, Staffordshire & more with local house removals, man and van, packing and storage services. Eco-friendly, insured removals across the West Midlands.',
    ogTitle: 'Areas We Cover | VIP House Movers West Midlands',
    ogDescription: 'House removals across Wolverhampton, Birmingham, Walsall, Dudley, Telford and Bilston. VIP House Movers West Midlands.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/areas-we-cover',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Areas We Cover | VIP House Movers West Midlands',
    twitterDescription: 'Removals across Wolverhampton, Birmingham, Walsall, Dudley & more from VIP House Movers.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/areas-we-cover');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'home removals, house removals, moving company, removal company, removals service, local removals, furniture removals, moving services, house moving service, professional movers, removals near me, house removals near me, local moving company near me, cheap removals West Midlands, removals company West Midlands, man and van West Midlands, moving services West Midlands, house movers West Midlands, trusted removals company',
      },
    ],
  });
  return (
    <SiteShell activePath="/areas-we-cover" fixedNav areaMenuPath="/areas-we-cover">
      <PageHero
        eyebrow="VIP House Movers Areas We Cover"
        title="Professional removals across the West Midlands and beyond"
        description="Explore the locations VIP House Movers serves for house removals, office moves, packing support, and premium relocation services tailored to each area."
        primaryLabel="Get a Free Quote"
        primaryTo="/quote"
        secondaryLabel="Contact VIP House Movers"
        secondaryTo="/contact"
        backgroundImage={areasBg}
      />

      {/* Location Navigation - Improves SEO */}
      <section className="bg-white py-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Browse All Locations</h2>
          <LocationLinks />
        </div>
      </section>

      <section className="py-24 bg-gray-50 relative overflow-hidden border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="mb-14 max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-white px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#B78D19] shadow-sm">
              Areas We Cover
            </div>
            <h2 className="mt-6 text-4xl font-bold tracking-tight text-gray-900">Choose your location</h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-600">
              VIP House Movers supports customers across key towns and cities with a premium removals service designed around careful handling, efficient transport, and dependable communication.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
            {areaPages.map((area) => (
              <Link
                key={area.path}
                to={area.path}
                className="group rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40 hover:shadow-xl hover:shadow-[#D4AF37]/10 overflow-hidden"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={imageForArea(area.path)}
                    alt={`VIP House Movers providing professional removals and moving services in ${area.cardTitle}`}
                    width={1200}
                    height={600}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-wider text-gray-900">
                    <MapPinned className="h-4 w-4 text-[#D4AF37]" />
                    {area.cardTitle}
                  </div>
                </div>
                <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{area.cardTitle}</h3>
                <p className="text-gray-600 font-light leading-relaxed mb-6">
                  {area.description}
                </p>
                <div className="space-y-3">
                  {area.highlights.slice(0, 3).map((highlight) => (
                    <div key={highlight} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#D4AF37]" />
                      <span className="text-gray-700">{highlight}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-[#B78D19]">
                  View area page
                  <span aria-hidden="true">→</span>
                </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">Location pages that strengthen local removals SEO</h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-600">
                Every area page helps VIP House Movers rank more effectively for local intent searches such as house removals Wolverhampton, movers in Dudley, or removals company Birmingham. The content is designed to match how customers search by town, service need, and move type.
              </p>
            </div>
            <div className="rounded-3xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900">Examples of local search intent covered</h3>
              <ul className="mt-6 space-y-3 text-gray-700">
                {[
                  'house removals Wolverhampton',
                  'removals in Walsall',
                  'man and van Dudley',
                  'removal company Bilston',
                  'house removals Telford',
                  'cheap removals Wolverhampton',
                  'moving company Wolverhampton',
                  'house movers near me',
                  'packing services Birmingham',
                  'storage solutions West Midlands',
                ].map((term) => (
                  <li key={term} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-[#D4AF37]" />
                    <span>{term}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-20 relative border-t-8 border-[#D4AF37]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6 tracking-tight">Need help planning a move in your area?</h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto font-light">
            VIP House Movers can tailor your move around your property type, access requirements, moving date, and the level of support you need.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link to="/quote" className="inline-flex justify-center items-center gap-3 bg-[#D4AF37] text-gray-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:scale-105 transition-all duration-300 shadow-lg shadow-[#D4AF37]/20">
              Request a Custom Quote
            </Link>
            <Link to="/contact" className="inline-flex justify-center items-center gap-3 rounded-xl border border-white/20 bg-white/5 px-8 py-4 text-lg font-bold text-white transition-all duration-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">
              Speak to Our Team
            </Link>
          </div>
          <div className="mt-10 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-gray-200">
            <Compass className="h-4 w-4 text-[#D4AF37]" />
            Covering homes, flats, offices, and specialist moves
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

